// Mock data for Node-Boot Test Framework

export const setupHooks = [
  {
    id: 'useConfig',
    name: 'useConfig',
    signature: '(config: object) => void',
    category: 'setup',
    description: 'Override application configuration for tests',
    example: `useConfig({
  app: {port: 3001},
  database: {url: "test.db"},
  redis: {host: "localhost", port: 6380},
});`,
    documentation: `# useConfig Hook

The \`useConfig\` hook allows you to override application configuration for your tests.

## Signature
\`\`\`typescript
useConfig(config: object): void
\`\`\`

## Usage
Override any configuration values needed for your test environment:

\`\`\`typescript
useConfig({
  app: {port: 3001},
  database: {url: "test.db"},
  redis: {host: "localhost", port: 6380},
});
\`\`\`

## Best Practices
- Use test-specific ports to avoid conflicts
- Configure in-memory databases for faster tests
- Keep configuration changes minimal and focused`
  },
  {
    id: 'useEnv',
    name: 'useEnv',
    signature: '(variables: Record<string, string>) => void',
    category: 'setup',
    description: 'Set environment variables for the test session',
    example: `useEnv({
  NODE_ENV: "test",
  API_KEY: "test-key",
  DEBUG: "true",
});`,
    documentation: `# useEnv Hook

Set environment variables for your test session.

## Signature
\`\`\`typescript
useEnv(variables: Record<string, string>): void
\`\`\`

## Usage
\`\`\`typescript
useEnv({
  NODE_ENV: "test",
  API_KEY: "test-key",
  DEBUG: "true",
});
\`\`\`

## Best Practices
- Set NODE_ENV to "test" for test-specific behavior
- Use test-specific API keys
- Enable debug mode for troubleshooting`
  },
  {
    id: 'useMock',
    name: 'useMock',
    signature: '<T>(ServiceClass, mockImplementation) => void',
    category: 'setup',
    description: 'Mock service methods with automatic cleanup',
    example: `useMock(EmailService, {
  sendEmail: () => Promise.resolve(),
  validateEmail: () => true,
});`,
    documentation: `# useMock Hook

Mock service methods with automatic cleanup.

## Signature
\`\`\`typescript
useMock<T>(ServiceClass: new (...args: any[]) => T, mock: Partial<T>): void
\`\`\`

## Usage
\`\`\`typescript
// Mock with plain function implementations
useMock(EmailService, {
  sendEmail: () => Promise.resolve(),
  validateEmail: () => true,
});

// Mock with complex implementations
useMock(PaymentService, {
  processPayment: () => ({success: true, transactionId: "test-123"}),
});
\`\`\`

## Best Practices
- Mock external dependencies
- Keep internal services real for integration testing
- Provide realistic mock implementations`
  },
  {
    id: 'usePactum',
    name: 'usePactum',
    signature: '(baseUrl?: string) => void',
    category: 'setup',
    description: 'Enable Pactum.js integration for HTTP testing',
    example: `usePactum();
// or
usePactum("http://localhost:3001");`,
    documentation: `# usePactum Hook

Enable Pactum.js integration for HTTP testing.

## Signature
\`\`\`typescript
usePactum(baseUrl?: string): void
\`\`\`

## Usage
\`\`\`typescript
usePactum(); // Uses default server address
// or
usePactum("http://localhost:3001"); // Custom base URL

// Now you can use spec() from pactum directly in tests
\`\`\`

## Features
- Automatic base URL configuration
- Integration with test server
- Powerful assertion library`
  },
  {
    id: 'useCleanup',
    name: 'useCleanup',
    signature: '(hooks: { afterAll?: () => void, afterEach?: () => void }) => void',
    category: 'setup',
    description: 'Register cleanup functions for automatic execution',
    example: `useCleanup({
  afterAll: () => {
    // Clean up test data
  },
  afterEach: () => {
    // Reset state
  },
});`,
    documentation: `# useCleanup Hook

Register cleanup functions that will be called automatically.

## Signature
\`\`\`typescript
useCleanup(hooks: {
  afterAll?: () => void,
  afterEach?: () => void
}): void
\`\`\`

## Usage
\`\`\`typescript
useCleanup({
  afterAll: () => {
    // Clean up test data, close connections, etc.
  },
  afterEach: () => {
    // Reset state between tests
  },
});
\`\`\`

## Best Practices
- Clean up external resources in afterAll
- Reset state between tests in afterEach
- Ensure cleanup functions are idempotent`
  },
  {
    id: 'useAddress',
    name: 'useAddress',
    signature: '(callback: (address: string) => void) => void',
    category: 'setup',
    description: "Access the server's listening address after startup",
    example: `useAddress(address => {
  console.log("Server running at:", address);
});`,
    documentation: `# useAddress Hook

Access the server's listening address after startup.

## Signature
\`\`\`typescript
useAddress(callback: (address: string) => void): void
\`\`\`

## Usage
\`\`\`typescript
useAddress(address => {
  console.log("Server running at:", address);
  // Set up external test dependencies
});
\`\`\`

## Use Cases
- Configure external services with test server URL
- Log server information for debugging
- Set up webhooks for integration tests`
  }
];

export const testHooks = [
  {
    id: 'useService',
    name: 'useService',
    signature: '<T>(ServiceClass) => T',
    category: 'test',
    description: 'Get service instances from the IoC container',
    example: `const userService = useService(UserService);
const result = userService.findUser("123");`,
    documentation: `# useService Hook

Get service instances from the IoC container.

## Signature
\`\`\`typescript
useService<T>(ServiceClass: new (...args: any[]) => T): T
\`\`\`

## Usage
\`\`\`typescript
const userService = useService(UserService);
const result = userService.findUser("123");
\`\`\`

## Features
- Direct access to application services
- Fully initialized with dependencies
- Real service instances for integration testing`
  },
  {
    id: 'useRepository',
    name: 'useRepository',
    signature: '<T>(RepositoryClass) => T',
    category: 'test',
    description: 'Get repository instances for data layer testing',
    example: `const userRepo = useRepository(UserRepository);
await userRepo.create({name: "Test User"});`,
    documentation: `# useRepository Hook

Get repository instances for data layer testing.

## Signature
\`\`\`typescript
useRepository<T>(RepositoryClass: new (...args: any[]) => T): T
\`\`\`

## Usage
\`\`\`typescript
const userRepo = useRepository(UserRepository);
await userRepo.create({name: "Test User"});
const users = await userRepo.findAll();
\`\`\`

## Features
- Direct database access
- Integration with NodeBoot persistence layer
- Full CRUD operations`
  },
  {
    id: 'useHttp',
    name: 'useHttp',
    signature: '(baseURL?: string) => HttpClient',
    category: 'test',
    description: 'Get HTTP client for API testing',
    example: `const {get, post} = useHttp();
const users = await get("/api/users");`,
    documentation: `# useHttp Hook

Get HTTP client for API testing.

## Signature
\`\`\`typescript
useHttp(baseURL?: string): HttpClient
\`\`\`

## Usage
\`\`\`typescript
const {get, post, put, delete: del} = useHttp();

// GET request
const users = await get("/api/users");

// POST request with data
const newUser = await post("/api/users", {
  name: "John Doe",
  email: "john@example.com",
});

// With headers
const response = await get("/api/protected", {
  headers: {Authorization: "Bearer token"},
});
\`\`\`

## Features
- Automatic base URL configuration
- Full HTTP method support
- Custom headers and options`
  },
  {
    id: 'useSupertest',
    name: 'useSupertest',
    signature: '() => TestAgent',
    category: 'test',
    description: 'Get Supertest instance for HTTP testing with assertions',
    example: `const request = useSupertest();
await request.get("/api/users").expect(200);`,
    documentation: `# useSupertest Hook

Get Supertest instance for HTTP testing with built-in assertions.

## Signature
\`\`\`typescript
useSupertest(): TestAgent
\`\`\`

## Usage
\`\`\`typescript
const request = useSupertest();

await request
  .get("/api/users")
  .expect(200)
  .expect("Content-Type", /json/)
  .expect(res => {
    expect(res.body).toHaveLength(1);
  });
\`\`\`

## Features
- Built-in assertion methods
- Chainable API
- Integration with test server`
  },
  {
    id: 'useSpy',
    name: 'useSpy',
    signature: '<T>(ServiceClass, methodName: string) => SpiedFunction',
    category: 'test',
    description: 'Create spies on service methods',
    example: `const spy = useSpy(EmailService, "sendEmail");
assert.equal(spy.callCount, 1);`,
    documentation: `# useSpy Hook

Create spies on service methods.

## Signature
\`\`\`typescript
useSpy<T>(ServiceClass: new (...args: any[]) => T, methodName: keyof T & string): SpiedFunction
\`\`\`

## Usage
\`\`\`typescript
const spy = useSpy(EmailService, "sendEmail");

// Execute code that calls the method
await userService.register({email: "test@example.com"});

// Verify spy
assert.equal(spy.callCount, 1);
assert.equal(spy.calls[0].args[0], "test@example.com");
\`\`\`

## Features
- Method call tracking
- Argument inspection
- Call count verification`
  },
  {
    id: 'useTimer',
    name: 'useTimer',
    signature: '() => { control, tracking }',
    category: 'test',
    description: 'Control fake timers and track execution time',
    example: `const {control, tracking} = useTimer();
control().advanceTimeBy(1000);`,
    documentation: `# useTimer Hook

Control fake timers and track execution time.

## Signature
\`\`\`typescript
useTimer(): {
  control: () => TimeControl,
  tracking: () => TimeTracking
}
\`\`\`

## Usage
\`\`\`typescript
const {control, tracking} = useTimer();

// Advance time
control().advanceTimeBy(1000);

// Track execution time
const timer = tracking().start();
// ... execute code ...
const duration = timer.end();
\`\`\`

## Features
- Fake timer control
- Execution time tracking
- Test time-dependent code`
  }
];

export const coreFeatures = [
  {
    id: 'plugin-architecture',
    name: 'Plugin Architecture',
    description: 'Everything is a hook that can be added, removed, or customized',
    icon: 'Puzzle'
  },
  {
    id: 'lifecycle-driven',
    name: 'Lifecycle-Driven',
    description: 'Clear, predictable execution phases for test setup and teardown',
    icon: 'RefreshCw'
  },
  {
    id: 'priority-based',
    name: 'Priority-Based',
    description: 'Hooks execute in controlled order based on priority',
    icon: 'ArrowUpDown'
  },
  {
    id: 'state-management',
    name: 'State Management',
    description: 'Hooks can store and share state across lifecycle phases',
    icon: 'Database'
  },
  {
    id: 'test-runner-agnostic',
    name: 'Test Runner Agnostic',
    description: 'Core framework works with any test runner',
    icon: 'Layers'
  },
  {
    id: 'composable',
    name: 'Composable',
    description: 'Hook libraries can extend and combine functionality',
    icon: 'GitMerge'
  }
];
